import {getMockDb, reloadMockDb, updateMockDb} from '../mockDb';
import {allMockClientUsers} from '../fixtures';
import {items as saasItems, workspaceOverride as saasWsOverride, docContents as saasDocContents} from './saas-onboarding';
import {items as pizzaItems, workspaceOverride as pizzaWsOverride, docContents as pizzaDocContents} from './pizza-chain';
import {items as beautyItems, workspaceOverride as beautyWsOverride, docContents as beautyDocContents} from './beauty-salon-chain';
import {items as retailItems, workspaceOverride as retailWsOverride, docContents as retailDocContents, wsUsers as retailWsUsers, taskListUnits as retailTaskListUnits, tasks as retailTasks, taskReports as retailTaskReports} from './retail-chain';
import {locations as retailLocations, issues as retailIssues, auditTemplates as retailAuditTemplates, auditQuestions as retailAuditQuestions, auditRuns as retailAuditRuns, auditAnswers as retailAuditAnswers} from './retail-chain-reliability';

const MOCK_DB_KEY = 'lgc-mock-db-v2';
const DOC_KEY_PREFIX = 'lg_d_';
const SEEDED_SCENARIO_KEY = 'lgc-demo-scenario';
// Bump this string whenever scenario content changes. On mismatch the app
// re-seeds automatically so deployed updates aren't hidden by stale localStorage.
const SEED_VERSION = '20260710-1';
const SEED_VERSION_KEY = 'lgc-demo-seed-version';

export interface Scenario {
    name: string;
    label: string;
}

export const SCENARIOS: Scenario[] = [
    {name: 'saas-onboarding', label: 'SaaS onboarding'},
    {name: 'pizza-chain', label: 'Pizza franchise'},
    {name: 'beauty-salon-chain', label: 'Beauty salon chain'},
    {name: 'retail-chain', label: 'Retail chain (8 stores)'},
];

function clearDocEntries() {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(DOC_KEY_PREFIX));
    keys.forEach(k => localStorage.removeItem(k));
}

function seedDocs(docContents: Record<string, any>) {
    for (const [itemId, doc] of Object.entries(docContents)) {
        const state = {
            docId: itemId,
            doc,
            version: 0,
            latestEditor: '',
            updatedAt: new Date().toISOString(),
            comments: {version: 0, comments: []},
            commentVersion: 0,
        };
        try {
            localStorage.setItem(`${DOC_KEY_PREFIX}${itemId}`, JSON.stringify(state));
        } catch (e) {
            console.warn('[mock-backend] failed to seed doc', itemId, e);
        }
    }
}

type ScenarioData = {
    items: any[];
    workspaceOverride: Record<string, any>;
    docContents: Record<string, any>;
    wsUsers?: any[];
    taskListUnits?: any[];
    tasks?: any[];
    taskReports?: any[];
    // Reliability + Audits ("Фича аудит")
    locations?: any[];
    issues?: any[];
    auditTemplates?: any[];
    auditQuestions?: any[];
    auditRuns?: any[];
    auditAnswers?: any[];
};

function getScenarioData(scenario: string): ScenarioData {
    if (scenario === 'pizza-chain') {
        return {items: pizzaItems, workspaceOverride: pizzaWsOverride, docContents: pizzaDocContents};
    }
    if (scenario === 'beauty-salon-chain') {
        return {items: beautyItems, workspaceOverride: beautyWsOverride, docContents: beautyDocContents};
    }
    if (scenario === 'retail-chain') {
        return {
            items: retailItems, workspaceOverride: retailWsOverride, docContents: retailDocContents,
            wsUsers: retailWsUsers, taskListUnits: retailTaskListUnits, tasks: retailTasks, taskReports: retailTaskReports,
            locations: retailLocations, issues: retailIssues,
            auditTemplates: retailAuditTemplates, auditQuestions: retailAuditQuestions,
            auditRuns: retailAuditRuns, auditAnswers: retailAuditAnswers,
        };
    }
    return {items: saasItems, workspaceOverride: saasWsOverride, docContents: saasDocContents};
}

function applySeed(scenario: string) {
    const {
        items, workspaceOverride, docContents, wsUsers = [], taskListUnits = [], tasks = [], taskReports = [],
        locations = [], issues = [], auditTemplates = [], auditQuestions = [], auditRuns = [], auditAnswers = [],
    } = getScenarioData(scenario);

    updateMockDb(db => {
        db.items = [...items];
        db.clientUsers = allMockClientUsers.map(u => ({...u}));
        db.wsUsers = [...wsUsers];
        db.taskListUnits = [...taskListUnits];
        db.tasks = [...tasks];
        db.taskReports = [...taskReports];
        db.locations = [...locations];
        db.issues = [...issues];
        db.auditTemplates = [...auditTemplates];
        db.auditQuestions = [...auditQuestions];
        db.auditRuns = [...auditRuns];
        db.auditAnswers = [...auditAnswers];
        if (db.workspaces.length > 0) {
            Object.assign(db.workspaces[0], workspaceOverride);
        }
    });

    clearDocEntries();
    seedDocs(docContents);
    localStorage.setItem(SEEDED_SCENARIO_KEY, scenario);
    localStorage.setItem(SEED_VERSION_KEY, SEED_VERSION);
}

/**
 * Called during mock bootstrap. Seeds scenario data if:
 * - The ?demo= URL param names a scenario (re-seeds every time it's present), OR
 * - No db has been seeded yet (first visit), OR
 * - SEED_VERSION doesn't match (new bundle deployed — wipe stale localStorage and re-seed).
 */
export function seedIfNeeded(): void {
    const urlScenario = new URLSearchParams(window.location.search).get('demo');

    if (urlScenario) {
        const scenario = SCENARIOS.find(s => s.name === urlScenario)?.name ?? 'retail-chain';
        localStorage.removeItem(MOCK_DB_KEY);
        reloadMockDb();
        applySeed(scenario);
        return;
    }

    const currentDb = getMockDb();
    const storedVersion = localStorage.getItem(SEED_VERSION_KEY);

    if (currentDb.items.length === 0 || storedVersion !== SEED_VERSION) {
        const versionMismatch = storedVersion !== SEED_VERSION;
        // On a version mismatch always reset to the default scenario so stale scenario
        // choices don't survive a breaking bundle update. On a fresh DB keep any stored choice.
        const scenario = versionMismatch
            ? 'retail-chain'
            : (localStorage.getItem(SEEDED_SCENARIO_KEY) ?? 'retail-chain');
        localStorage.removeItem(MOCK_DB_KEY);
        reloadMockDb();
        applySeed(scenario);
        // On a version-triggered re-seed: wipe the DataSource IDB cache so the
        // DataSourcePersistence doesn't serve stale items from the previous seed,
        // then reload so the app starts fresh with the correct localStorage data.
        if (versionMismatch) {
            try { indexedDB.deleteDatabase('LGCCache'); } catch {}
            window.location.reload();
        }
    }
}

/**
 * Resets all demo state and re-seeds from the chosen scenario, then reloads.
 * Call from the DemoPanel "Reset demo" button.
 */
export async function resetAndSeed(scenario: string = 'retail-chain'): Promise<void> {
    // Clear mockDb
    localStorage.removeItem(MOCK_DB_KEY);
    // Clear persisted DataSource cache (best-effort — IndexedDB may be unavailable)
    try {
        const {cacheManager} = await import('@stores/cache/CacheManager');
        await cacheManager.clear();
    } catch {}
    // Clear doc entries
    clearDocEntries();
    // Clear collab version tracker keys and demo persona (reset goes back to manager view)
    localStorage.removeItem('lgc_first_used');
    localStorage.removeItem('lgc-demo-persona');
    // Re-seed and reload
    reloadMockDb();
    applySeed(scenario);
    window.location.reload();
}

export function getCurrentScenario(): string {
    return localStorage.getItem(SEEDED_SCENARIO_KEY) ?? 'retail-chain';
}
